import { format, parseISO } from 'date-fns';
import { costFormatter } from '@format/currency';
import { getPricePropertyName } from '../../products';
import { productTypeReference } from '../../products/constants';
import { ADDON_PRODUCT_TYPES, ADDON_PRODUCT_TYPES_LIST } from '../constants';

export const formatDate = date => format(parseISO(date), 'EEEE, MMMM d');

export const calculateGrandTotals = orders => {
  const totals = orders.reduce(
    (acc, order) => {
      acc.totalTax += parseFloat(order.total_tax);
      acc.subtotal += parseFloat(order.total_ex_tax);
      acc.totalPrice += parseFloat(order.total_price);
      acc.discountAmount += parseFloat(order.total_discount);

      acc.orderTotal += parseFloat(order.subtotal_ex_tax);

      return acc;
    },
    {
      totalTax: 0,
      subtotal: 0,
      totalPrice: 0,
      discountAmount: 0,
      orderTotal: 0,
    },
  );
  totals.totalTax = totals.totalTax > 0 ? costFormatter(totals.totalTax) : '';
  totals.subtotal = costFormatter(totals.subtotal);
  totals.totalPrice = costFormatter(totals.totalPrice);
  totals.discountAmount = costFormatter(totals.discountAmount);
  totals.orderTotal = costFormatter(totals.orderTotal);
  return totals;
};

export const formatDeliveryAddress = address => ({
  street: [address.address1, address.address2].filter(Boolean).join(', '),
  city: address.city,
  state: address.state,
  zip: address.zip_code,
});

export const formatPets = pets =>
  pets.map(pet => ({
    ...pet,
    breeds: pet.breeds.map(breed => breed.name),
  }));

export const formatOrderSummary = (
  pets,
  subscriptions,
  orders,
  products,
  addons,
  period,
) =>
  pets.map(pet => {
    const order = orders.find(order => order.items[0].pet_id === pet.id);
    const foodDetails = formatFoodDetails(order);

    const { weeks_of_food } = order.items.find(
      item => item.weeks_of_food !== null,
    );

    const {
      next_delivery_target,
      frequency,
      sku,
      id: subscription_id,
    } = subscriptions.find(subscription => subscription.pet_id === pet.id);
    const pricePerPeriodIndex = getPricePropertyName(period);
    const {
      variants: [{ recipes, [pricePerPeriodIndex]: pricePerPeriod }],
      portion_size,
    } = products.find(product => product.variants[0].sku === sku);

    const currentPetsAddons = addons[subscription_id];

    return {
      petName: pet.name,
      breeds: pet.breeds.map(breed => breed.name),
      price: costFormatter(foodDetails.foodTotalPrice),
      discountedPrice: costFormatter(foodDetails.foodDiscountedPrice),
      pricePerPeriod: costFormatter(pricePerPeriod),
      frequencyInWeeks: frequency,
      weeksOfFood: weeks_of_food,
      deliveryNext: formatDate(next_delivery_target),
      recipes,
      addons: formatAddonPrices(order.items, currentPetsAddons),
      portionSize: portion_size,
    };
  });

const formatAddonPrices = (items, addons) =>
  items.reduce((accumulator, item) => {
    /*
     * Note that this will fail if someone immediately changes their subscription addons
     * and ends up back on this page. This is unlikely, so not going to account for it
     * now (or maybe ever). The fix would be to load addons from the order rather than
     * the subscription on the CheckoutConfirmationContainer.
     */
    const addon = addons.find(
      addon =>
        addon.sku === item.sku &&
        item.price !== '0.00' &&
        ADDON_PRODUCT_TYPES_LIST.includes(item.product_type),
    );

    if (addon) {
      accumulator.push({
        ...addon,
        // Make sure we use the price from the order, which includes the discount.
        price: costFormatter(item.price * item.quantity),
        discounted_price: costFormatter(item.discounted_price * item.quantity),
      });
    }
    return accumulator;
  }, []);

const formatFoodDetails = order => {
  const items = order.items?.filter(
    item =>
      item.product_type !== ADDON_PRODUCT_TYPES.treats &&
      item.product_type !== ADDON_PRODUCT_TYPES.supplements,
  );

  return {
    foodTotalPrice: items.reduce(
      (total, item) => (total += parseFloat(item.price)),
      0,
    ),
    foodDiscountedPrice: items.reduce(
      (total, item) => (total += parseFloat(item.discounted_price)),
      0,
    ),
  };
};

export const getConfirmationOrderSummaryProps = (
  pricingList,
  offer,
  orderList,
  pets,
  products,
) => {
  let plans = [];
  let pricingListProps;

  if (
    !orderList ||
    !products ||
    !pricingList ||
    Object.values(pricingList).some(value => value === undefined)
  ) {
    return {};
  }

  pricingListProps = {
    ...pricingList,
    discountText: `${offer?.formattedAmount} off`,
    discountAmount: orderList.reduce(
      (total, order) => total + Number(order.total_discount),
      0,
    ),
  };

  const lineItems = orderList.flatMap(order => order.items);

  plans = pets?.map(({ id, name }) => {
    const lineItem = lineItems.find(
      item => item.pet_id === id && item.product_type in productTypeReference,
    );
    const petId = lineItem?.pet_id;
    const product = products.find(
      product => product.variants[0].sku === lineItem.sku,
    );

    return {
      petId,
      petName: name,
      productType: product?.product_type || lineItem?.product_type,
    };
  });

  return {
    plans,
    formattedPricingList: pricingListProps,
  };
};

export const getWebConfirmationOrderSummaryProps = (
  offer,
  orderList,
  pets,
  products,
) => {
  let plans = [];
  let pricingListProps;

  if (!orderList || !products) {
    return {};
  }

  pricingListProps = {
    discountLabel: `DISCOUNT`,
    discountText: `${offer?.formattedAmount} off`,
    discountAmount: orderList.reduce(
      (total, order) => total + Number(order.total_discount),
      0,
    ),
  };

  const lineItems = orderList.flatMap(order => order.items);

  plans = pets?.map(({ id, name }) => {
    const lineItem = lineItems.find(
      item => item.pet_id === id && item.product_type in productTypeReference,
    );
    const petId = lineItem?.pet_id;
    const product = products.find(
      product => product.variants[0].sku === lineItem?.sku,
    );

    return {
      petId,
      petName: name,
      productType: product?.product_type || lineItem?.product_type,
    };
  });

  return {
    plans,
    formattedPricingList: pricingListProps,
  };
};

export const getOptionCallout = ({ frequencyCallouts, frequencies, i }) => {
  const currentCallout = frequencyCallouts[i] || '';
  const middleOptionIndex =
    frequencies?.length % 2 === 0
      ? frequencies?.indexOf(
          frequencies[Math.floor(frequencies?.length / 2) - 1],
        )
      : frequencies?.indexOf(frequencies[Math.floor(frequencies?.length / 2)]);
  const lastOption = i + 1 === frequencies?.length;
  //if there are more than three options, should only display a callout on the
  // first, middle, and last options
  const noCalloutOption =
    frequencies?.length > 3 &&
    i !== 0 &&
    i !== middleOptionIndex &&
    !lastOption;

  return frequencies?.length > 1 && !noCalloutOption
    ? `(${
        frequencies?.length > 3 && i === middleOptionIndex
          ? frequencyCallouts[1]
          : lastOption
          ? frequencyCallouts[2]
          : currentCallout
      })`
    : '';
};
